import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

// Third Party

// MUI Components
import { 
    Box, 
    Typography, 
    Menu, 
    MenuItem, 
    Divider,
    Modal,
    IconButton,
    Tooltip,
    Fab
} from '@mui/material'

// MUI Icons
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import SubjectIcon from '@mui/icons-material/Subject';

// PropelAuth
import { useAuthInfo } from '@propelauth/react'

// CSS
import '../../App.css'

// Custom Components
import { EditTemplateSectionContent } from './components/EditTemplateSectionContent'
import OnlyOfficeViewerComponent from '../DocumentPage/DocViewer'
import TrainingSetModal from '../../components/TrainingSetModal'
import PDFWebViewComponent from '../../components/PDFWebViewComponent'
import DeleteTemplateConfirmationDialog from '../../pages/TemplatesPages/components/dialogs/DeleteTemplateConfirmationDialog'
import TemplateContentsSidebar from './components/TemplateContentsSidebar'
import TemplateSectionEditorHeader from './components/TemplateSectionEditorHeader'
import UsersAndPermissionsModal from '../SettingsPage/sections/UsersAndPermissionsSections/modals/UsersAndPermissionsModal'

// Context
import { useInstructionalMRT } from '../../contexts/InstructionalMRTContext'
import { useWebViewer } from '../../contexts/WebViewerContext'
import { useUser } from '../../contexts/UserContext'

// Utils
import {
    generatePresignedUrl,
    updateRoles,
    inviteUserToOrganization,
    listUsersInOrganization
} from '../../utils/utils'

// CONSTANTS
// const TESTING = true

function EditTemplatePage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [documentUrl, setDocumentUrl] = useState('')
    const [templateData, setTemplateData] = useState([])
    const [isTrainingSetModalOpen, setIsTrainingSetModalOpen] = useState(false)
    const [isDeleteSectionDialogOpen, setIsDeleteSectionDialogOpen] = useState(false)
    const [document, setDocument] = useState({})
    const [menuAnchorEl, setMenuAnchorEl] = useState(null)
    const [hasToggledContent, setHasToggledContent] = useState({})
    const [documentKey, setDocumentKey] = useState('')
    const [sectionMenuAnchorEl, setSectionMenuAnchorEl] = useState(null)
    const [sectionMenuSection, setSectionMenuSection] = useState(null)
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
    const [isDocumentCollapsed, setIsDocumentCollapsed] = useState(false)
    const [addUsersModalOpen, setAddUsersModalOpen] = useState(false)
    const [allUsers, setAllUsers] = useState([])

    // Add ref for PDF viewer
    const pdfViewerRef = useRef(null)
    
    const auth = useAuthInfo()
    const org = auth.orgHelper.getOrgs()[0].orgId
    const { roles } = useUser()

    // Check if user is a Member (read-only access)
    const isMemberRole = roles.includes('Member')

    // Context
    const {
        instructionalMRT,
        loadInstructionalMRT,
        selectSectionForEdit,
        deleteSection,
        selectedSection,
        updateSelectedSection
    } = useInstructionalMRT()

    const { webViewerInstance } = useWebViewer()
    
    // Generate stable document key based on document ID and version
    const generateDocumentKey = (docId, lastUpdated) => {
        // Create a stable key using document ID and last update timestamp
        // Ensure it meets OnlyOffice requirements: max 20 chars, alphanumeric plus -_=
        if (!docId) {
            console.warn('No document ID provided for key generation')
            return `temp-${Date.now().toString(36).substring(0, 10)}`
        }
        
        const timestamp = lastUpdated ? new Date(lastUpdated).getTime() : Date.now()
        const baseKey = `${docId}-${timestamp}`
        // Hash it to ensure consistent length and valid characters
        const hash = baseKey.split('').reduce((acc, char) => {
            return ((acc << 5) - acc + char.charCodeAt(0)) | 0
        }, 0)
        // Convert to base36 and ensure it's within 20 characters
        const key = `doc-${Math.abs(hash).toString(36).substring(0, 15)}`
        console.log('Generated document key:', key, 'from:', { docId, lastUpdated })
        return key
    }


    const handleSectionMenuClose = () => {
        setSectionMenuAnchorEl(null)
        setSectionMenuSection(null)
    }

    // Three dots menu handlers
    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchorEl(null)
    }


    const handleDelete = () => {
        if (isMemberRole) {
            console.log('Members cannot delete templates')
            handleMenuClose()
            return
        }
        console.log('Delete clicked')
        handleMenuClose()
    }


    const handleBackClick = () => {
        // Save all accumulated changes before navigating back
        navigate('/templates')
    }

    const handleUsersClick = () => {
        // Simply open the modal - users are already loaded from the template data
        setAddUsersModalOpen(true)
    }

    const handleRoleChange = async (userId, newRole) => {
        // Update local state optimistically with proper nested structure
        setAllUsers(prevUsers =>
            prevUsers.map(user => {
                if (user.user_id === userId) {
                    // Update both direct role property and nested structure
                    const updatedUser = { ...user, role: newRole }

                    // Also update nested structure if it exists
                    if (user.org_id_to_org_info && user.org_id_to_org_info[org]) {
                        updatedUser.org_id_to_org_info = {
                            ...user.org_id_to_org_info,
                            [org]: {
                                ...user.org_id_to_org_info[org],
                                user_role: newRole
                            }
                        }
                    }

                    return updatedUser
                }
                return user
            })
        )

        try {
            const orgAccessToken = await auth.getAccessTokenSilently()
            const jsonPayload = {
                user_id: userId,
                role: newRole,
                org_id: org
            }

            await updateRoles(jsonPayload, orgAccessToken)

            // Reload all template data after successful role update
            await retrieveData()
        } catch (error) {
            console.error('Error updating role:', error)

            // Revert the optimistic update on error by reloading all data
            try {
                await retrieveData()
            } catch (revertError) {
                console.error('Error reverting role change:', revertError)
            }
        }
    }

    const handleInviteUser = async (email, role) => {
        console.log('[handleInviteUser] Starting - invited:', email, 'with role:', role)
        console.log('[handleInviteUser] Current allUsers count:', allUsers.length)

        try {
            // Reload ALL template data (users, document, presigned URLs, etc.)
            console.log('[handleInviteUser] Reloading all template data...')
            await retrieveData()

            console.log('[handleInviteUser] All data reloaded successfully')
        } catch (error) {
            console.error('[handleInviteUser] Error:', error)
        }
    }

    const handleTrainingSetModalClose = () => {
        setIsTrainingSetModalOpen(false)
    }

    const handleRemoveTrainingSet = (trainingSetId) => {
        const updatedTrainingSets = (document.training_sets || []).filter(
            set => set.id !== trainingSetId
        )
        setDocument(prev => ({
            ...prev,
            training_sets: updatedTrainingSets
        }))
    }

    const handleSaveTrainingSets = () => {
        // Future implementation for saving training sets
        console.log('Save training sets clicked')
        setIsTrainingSetModalOpen(false)
    }


    const handleRenameSection = (section) => {
        selectSectionForEdit(section)
        // TODO: Implement rename functionality
    }

    const handleDeleteSection = (section) => {
        if (isMemberRole) {
            console.log('Members cannot delete sections')
            return
        }
        console.log('Delete section:', section)
        setIsDeleteSectionDialogOpen(true)
        // Close the menu when opening the dialog
        setSectionMenuAnchorEl(null)
    }

    const handleConfirmDeleteSection = async () => {
        if (isMemberRole) {
            console.log('Members cannot delete sections')
            return
        }
        console.log('handleConfirmDeleteSection called', { sectionMenuSection })
        if (!sectionMenuSection) {
            console.error('No section selected for deletion')
            return
        }
        
        console.log('Starting deletion process')
        setLoading(true)
        try {
            console.log('Calling deleteSection with:', sectionMenuSection)
            await deleteSection(sectionMenuSection)
            console.log('Deletion successful, closing dialog')
            setIsDeleteSectionDialogOpen(false)
            // Clear the selected section after successful deletion
            setSectionMenuSection(null)
        } catch (error) {
            console.error('Error deleting section:', error)
        } finally {
            console.log('Resetting loading state')
            setLoading(false)
        }
    }


    const handleDeleteSectionDialogClose = () => {
        setIsDeleteSectionDialogOpen(false)
        // Clear the selected section when dialog is cancelled
        setSectionMenuSection(null)
    }

    // Reusable function to load all template data
    const retrieveData = async () => {
        try {
            setLoading(true)

            const orgAccessToken = await auth.tokens.getAccessTokenForOrg(org)

            const result = await loadInstructionalMRT(setLoading, setError, id, true)

            setDocument(result)

            // Extract and set users from the result (similar to DocumentSetsPage)
            const users = result['users'] || result.users
            console.log('[retrieveData] Template users:', users)
            setAllUsers(users || [])

            // Always generate stable document key based on document ID and last update
            const key = generateDocumentKey(result._id?.$oid || result._id || id, result.last_updated || result.created_at)
            setDocumentKey(key)

            if (result.url) {
                console.log("result.url: ", result.url)
                setDocumentUrl(result.url)
            } else {
                // Generate presigned URL
                if (!result['template_s3_uri']) {
                    throw new Error('No template S3 URI found in document data')
                }

                // Extract S3 key from URI - handle different S3 URI formats
                let s3Key = result['template_s3_uri']
                if (s3Key.startsWith('s3://')) {
                    // Remove s3:// prefix and bucket name
                    const parts = s3Key.substring(5).split('/')
                    s3Key = parts.slice(1).join('/')
                }

                console.log("s3Key: ", s3Key)

                const presignedUrl = await generatePresignedUrl(s3Key, orgAccessToken)

                if (!presignedUrl) {
                    throw new Error('Failed to generate presigned URL')
                }

                // Verify the URL is valid
                try {
                    new URL(presignedUrl.url, 'https://dev-artos-test.s3.us-east-1.amazonaws.com')
                } catch (urlError) {
                    console.error("Invalid presigned URL format:", presignedUrl.url)
                    throw new Error('Generated presigned URL is not valid')
                }

                setDocumentUrl(presignedUrl.url)
            }
            setError(false)
        } catch (error) {
            console.error(error)
            setError(true)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        retrieveData()
    }, [])

    // useEffect(() => {
    //     // Load instructional MRT into context - this will handle all the data loading
    //     loadInstructionalMRT(id, setLoading, setError, TESTING)
    //     console.log('instructionalMRT', instructionalMRT)
    //     console.log('document', document)
    // }, [id])

    // const regex = /(?<=<templatetext>)(.*?)(?=<\/templatetext>)/g

    // function getComponents() {
    //     const components = templateData.flatMap((section) => {
    //         if (section.contains_content && section.human_instructions.length > 0) {
    //             let optionCount = 0
    //             return section.human_instructions.flatMap((instruction) => {
    //                 console.log("instruction: ", instruction)
    //                 const componentArray = instruction.match(regex)
    //                 console.log("componentArray: ", componentArray)
    //                 if (componentArray) {
    //                     return componentArray.map((componentElement) => {
    //                         console.log("componentElement: ", componentElement)
    //                         optionCount++
    //                         return {
    //                             id: uuidv4(),
    //                             component: componentElement,
    //                             section_name: section.section_name,
    //                             value: `${formatSectionTextSync(section.section_name)} - Component ${optionCount}`
    //                         }
    //                     })
    //                 } else {
    //                     return []
    //                 }
    //             })
    //         } else {
    //             return []
    //         }
    //     })
    // }

    // useEffect(() => {
    //     getComponents()
    // }, [templateData])

    // Sync templateData with instructionalMRT from context
    useEffect(() => {
        console.log("instructionalMRT: ", instructionalMRT)
        if (instructionalMRT && instructionalMRT.length > 0) {
            setTemplateData(instructionalMRT)
            
            // Auto-select first section if no section is currently selected
            if (!selectedSection || Object.keys(selectedSection).length === 0) {
                selectSectionForEdit(instructionalMRT[0])
            }
        }
        console.log("templateData: ", templateData)
    }, [instructionalMRT, document])

    // Debug selectedSection
    useEffect(() => {
        console.log("Current selectedSection: ", selectedSection)
    }, [selectedSection])


    const handleSectionEditClick = (selected) => {
        console.log("selected: ", selected)
        console.log("selected section_id: ", selected.section_id)
        
        // Select the section for editing
        selectSectionForEdit(selected)

        const sectionName = selected.title?.replace(/^[\d.]+\s*/, '') || selected.section_name
        const newSectionName = selected.title?.replace(/^([\d.]+)(\s)(.*)/, "$1\t$3") || selected.section_name
        console.log("sectionName: ", sectionName)
        console.log("newSectionName: ", newSectionName)

        // Check if the document is a PDF or DOCX
        const documentIsPDF = isPDF(documentUrl || document.source_document_name)

        if (documentIsPDF) {
            // Handle PDF scrolling with Apryse
            handlePDFSectionScroll(sectionName)
        } else {
            // Handle DOCX scrolling with OnlyOffice
            handleDocxSectionScroll(sectionName, newSectionName)
        }
    }

    // Track connector instances to prevent memory leaks
    const connectorRef = useRef(null)

    // Clean up connector on unmount
    useEffect(() => {
        return () => {
            if (connectorRef.current) {
                try {
                    // Clean up any lingering connector
                    connectorRef.current = null
                } catch (error) {
                    console.warn('Error cleaning up connector:', error)
                }
            }
        }
    }, [])

    // Handle PDF section scrolling using Apryse
    const handlePDFSectionScroll = (searchText) => {
        try {
            // Search options for Apryse
            const searchOptions = {
                caseSensitive: true,
                wholeWord: false,
                wildcard: false,
                regex: true,
                searchUp: false,
                ambientString: false,
            }

            // Perform the search
            if (webViewerInstance.UI) {
                // Disable search panel to prevent it from being shown
                webViewerInstance.UI.disableElements(['searchPanel'])
                
                webViewerInstance.UI.searchTextFull(searchText, searchOptions)
                
                // Wait for search to complete and jump to first result
                setTimeout(() => {
                    try {
                        // Get search results through the Core API
                        const searchMode = webViewerInstance.Core.Search.Mode.PAGE_STOP | webViewerInstance.Core.Search.Mode.HIGHLIGHT
                        const searchQuery = searchText
                        
                        webViewerInstance.Core.documentViewer.textSearchInit(
                            searchQuery,
                            searchMode,
                            {
                                fullSearch: true,
                                caseSensitive: true,
                            },
                            (result) => {
                                if (result.resultCode === webViewerInstance.Core.Search.ResultCode.FOUND) {
                                    // Jump to the search result
                                    webViewerInstance.Core.documentViewer.displaySearchResult(result)
                                    console.log('Successfully scrolled to PDF section')
                                } else {
                                    console.warn('Section not found in PDF')
                                }
                            }
                        )
                    } catch (error) {
                        console.error('Error processing search results:', error)
                    }
                }, 300)
            } else {
                console.error('WebViewer instance not accessible')
            }
        } catch (error) {
            console.error('Error scrolling to PDF section:', error)
        }
    }

    // Handle DOCX section scrolling using OnlyOffice
    const handleDocxSectionScroll = (sectionName, newSectionName) => {
        try {
            var docEditor = window.DocEditor.instances['docxEditorTemplate']
            if (!docEditor) {
                console.error('Document editor not found')
                return
            }

            // Clean up previous connector if exists
            if (connectorRef.current) {
                connectorRef.current = null
            }

            const tableOfContentsProperties = {
                oProperties: {
                    searchString: newSectionName, // The text to search for
                    matchCase: false, // Case sensitive search
                    isForward: true, // Search direction
                },
                searchString: newSectionName, // The text to search for
                matchCase: false, // Case sensitive search
                isForward: true, // Search direction
            }
            const searchProperties = {
                oProperties: {
                    searchString: sectionName, // The text to search for
                    matchCase: true, // Case sensitive search
                    isForward: true, // Search direction
                },
                searchString: sectionName, // The text to search for
                matchCase: true, // Case sensitive search
                isForward: true, // Search direction
            }
            
            // Create new connector and store reference
            connectorRef.current = docEditor.createConnector()
            const currentConnector = connectorRef.current
            
            currentConnector.executeMethod(
                'SearchNext',
                [tableOfContentsProperties],
                function() {
                    // Only continue if this is still the current connector
                    if (connectorRef.current !== currentConnector) return
                    
                    currentConnector.executeMethod(
                        'GetSelectedText',
                        ["entirely"],
                        function(sentence) {
                            // Only continue if this is still the current connector
                            if (connectorRef.current !== currentConnector) return
                            
                            console.log("sentence: ", sentence)
                            if (sentence !== newSectionName) {
                                currentConnector.executeMethod(
                                    'SearchNext',
                                    [searchProperties],
                                    function() {
                                        // Only continue if this is still the current connector
                                        if (connectorRef.current !== currentConnector) return
                                        
                                        currentConnector.executeMethod(
                                            'SearchNext',
                                            [searchProperties]
                                        )
                                    }
                                )
                            }
                        }
                    )
                }
            )
        } catch (error) {
            console.error('Error scrolling to DOCX section:', error)
        }
    }

    const handleTextSelection = () => {
        var docEditor = window.DocEditor.instances['docxEditorTemplate']
        const connector = docEditor.createConnector()
        connector.executeMethod('GetSelectedText', ['entirely'], function() {
            // Text selection handling - currently not used but kept for future implementation
        })
    }

    useEffect(() => {
        let cursorConnector = null

        setTimeout(() => {
            if (window.DocEditor?.instances['docxEditorTemplate']) {
                const docEditor = window.DocEditor.instances['docxEditorTemplate']
                cursorConnector = docEditor.createConnector()
                cursorConnector.attachEvent(
                    'onTargetPositionChanged',
                    handleTextSelection
                )
            }
        }, 1000)

        return () => {
            try {
                if (
                    cursorConnector &&
                    window.DocEditor?.instances['docxEditorTemplate']
                ) {
                    cursorConnector.detachEvent(
                        'onTargetPositionChanged',
                        handleTextSelection
                    )
                    cursorConnector = null
                }
            } catch (error) {
                console.warn('Error cleaning up document editor events:', error)
            }
        }
    }, [documentUrl, templateData])

    // Generate title and subtitle from document data
    const templateTitle =
        document.template_name ||
        document.document_nickname ||
        (document.document_metadata && (document.document_metadata.template_name || document.document_metadata.document_nickname)) ||
        document.name ||
        'ArtosGo CSR Template'
    const templateType = document.detected_document_type || ''
    const exampleOutlineIds = document.example_outline_ids || []


    // Utility to get file extension
    const getFileExtension = (filenameOrUrl) => {
        if (!filenameOrUrl) return ''
        
        // Remove query parameters and hash from URL
        const cleanUrl = filenameOrUrl.split('?')[0].split('#')[0]
        
        // Extract filename from URL path
        const filename = cleanUrl.split('/').pop()
        
        // Get extension from filename
        const match = filename.match(/\.([docx|pdf]+)$/)

        return match ? match[1].toLowerCase() : ''
    }

    // Utility to check if file is PDF
    const isPDF = (filenameOrUrl) => getFileExtension(filenameOrUrl) === 'pdf'

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 64px)',
                overflow: 'hidden',
            }}
        >
            <TemplateSectionEditorHeader
                templateTitle={templateTitle}
                templateType={templateType}
                exampleOutlineIds={exampleOutlineIds}
                onBackClick={handleBackClick}
                onEditClick={() => {}}
                onTrainingSetsClick={() => {}}
                menuAnchorEl={menuAnchorEl}
                onMenuClick={handleMenuClick}
                onMenuClose={handleMenuClose}
                onDeleteClick={handleDelete}
                onUsersClick={handleUsersClick}
                usersCount={allUsers.length}
            />
            {/* Main Content */}
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
                {(document.sections || []).length > 0 && (
                    <> 
                        {/* Left Panel - Contents List */}
                        {isSidebarCollapsed ? (
                            <Tooltip 
                                title="Contents" 
                                placement="right"
                            >
                                <Fab
                                    sx={{
                                        position: 'absolute',
                                        left: '16px',
                                        top: '5%',
                                        transform: 'translateY(-50%)',
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        zIndex: 10,
                                        '&:hover': {
                                            backgroundColor: '#f3f4f6',
                                            color: '#1F477D'
                                        }
                                    }}
                                    onClick={() => setIsSidebarCollapsed(false)}
                                >
                                    <SubjectIcon sx={{ fontSize: '22px' }} />
                                </Fab>
                            </Tooltip>
                        ) : (
                            <TemplateContentsSidebar
                                templateData={templateData}
                                selectedSection={selectedSection}
                                onSectionClick={handleSectionEditClick}
                                isCollapsed={false}
                                onToggleCollapse={() => setIsSidebarCollapsed(true)}
                            />
                        )}

                        {/* Center Panel - Section Editor */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                borderRight: !isDocumentCollapsed ? '1px solid #e5e7eb' : 'none',
                                overflow: 'hidden',
                                backgroundColor: '#F9FAFB',
                                position: 'relative'
                            }}
                        >
                            {/* Section Content Editor */}
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    overflow: 'auto',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    backgroundColor: '#F9FAFB',
                                    // Styled scrollbar
                                    scrollbarWidth: 'thin', // Firefox
                                    scrollbarColor: '#888 #f1f1f1', // Firefox
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#888',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: '#555',
                                        }
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        // Apply 60% constraint when sidebars are collapsed, 100% when both are open
                                        maxWidth: (isSidebarCollapsed && isDocumentCollapsed) ? '60%' : (isSidebarCollapsed || isDocumentCollapsed) && !(isSidebarCollapsed && isDocumentCollapsed) ? '80%' : '100%',
                                        padding: '20px 24px'
                                    }}
                                >
                                    {(() => {
                                        console.log('Rendering center panel, selectedSection:', selectedSection)
                                        
                                        if (selectedSection) {
                                            return (
                                                <EditTemplateSectionContent
                                                    key={selectedSection.section_id || selectedSection.id}
                                                    section={selectedSection}
                                                    onUpdate={isMemberRole ? () => {} : updateSelectedSection}
                                                    hasToggledContent={hasToggledContent}
                                                    setHasToggledContent={setHasToggledContent}
                                                    isDocumentCollapsed={isDocumentCollapsed}
                                                    onToggleDocument={() => setIsDocumentCollapsed(false)}
                                                    readOnly={isMemberRole}
                                                />
                                            )
                                        } else {
                                            return (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        height: '100%',
                                                        color: '#9ca3af'
                                                    }}
                                                >
                                                    <Typography>Select a section to edit</Typography>
                                                </Box>
                                            )
                                        }
                                    })()}
                                </Box>
                            </Box>
                        </Box>
                    </>
                )}

                {/* Right Panel - Document Viewer */}
                {isDocumentCollapsed ? (
                    <Tooltip 
                        title="Reference file" 
                        placement="left"
                    >
                        <Fab
                            sx={{
                                position: 'absolute',
                                right: '16px',
                                top: '5%',
                                transform: 'translateY(-50%)',
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                zIndex: 10,
                                '&:hover': {
                                    backgroundColor: '#f3f4f6',
                                    color: '#4F46E5'
                                }
                            }}
                            onClick={() => setIsDocumentCollapsed(false)}
                        >
                            <DescriptionIcon sx={{ fontSize: '22px' }} />
                        </Fab>
                    </Tooltip>
                ) : (
                    <Box 
                    sx={{ width: (document.sections || []).length > 0 ? '40%' : '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                        {/* Document Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '12px 16px',
                                borderBottom: '1px solid #e5e7eb',
                                backgroundColor: 'white',
                                height: '45px'
                            }}
                        >
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px' 
                                }}
                            >
                                <IconButton
                                    onClick={() => setIsDocumentCollapsed(true)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: '16px', color: '#6b7280' }} />
                                </IconButton>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <AttachFileIcon sx={{ fontSize: '16px', color: '#6b7280' }} />
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: '#6b7280',
                                            fontSize: '14px',
                                            fontWeight: 400
                                        }}
                                    >
                                        Reference file: {document.source_document_name || '(Clinical_Report.docx)'}
                                    </Typography>
                                </Box>
                            </Box>
                            {/* <Button
                                variant="text"
                                sx={{
                                    color: '#3b82f6',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    padding: '4px 8px',
                                    minWidth: 'auto',
                                    '&:hover': {
                                        backgroundColor: '#f0f9ff'
                                    }
                                }}
                                onClick={handleReplace}
                            >
                                Replace
                            </Button> */}
                        </Box>

                        {/* Document Content */}
                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                            {documentUrl && documentKey ? (
                                isPDF(documentUrl || document.source_document_name) ? (
                                    <PDFWebViewComponent
                                        initialFile={documentUrl}
                                        ref={pdfViewerRef}
                                    />
                                ) : (
                                    <>
                                        {(() => {
                                            const fileType = getFileExtension(document.source_document_name || documentUrl) || 'docx'
                                            console.log('Debug - Document info:', {
                                                source_document_name: document.source_document_name,
                                                documentUrl: documentUrl,
                                                extractedFileType: fileType,
                                                documentKey: documentKey
                                            })
                                            return (
                                                <OnlyOfficeViewerComponent
                                                    documentId='docxEditorTemplate'
                                                    fileType={fileType}
                                                    initialFile={documentUrl}
                                                    onLoaded={() => {
                                                        console.log('Document loaded')
                                                    }}
                                                    documentName={document.source_document_name && !document.source_document_name.includes('.') ? `${document.source_document_name}.docx` : document.source_document_name}
                                                    documentKey={documentKey}
                                                    displayType='embedded'
                                                />
                                            )
                                        })()}
                                    </>
                                )
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: '#6b7280'
                                    }}
                                >
                                    {loading ? 'Loading document...' : 'No document available'}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>


            {/* Section Menu */}
            <Menu
                anchorEl={sectionMenuAnchorEl}
                open={Boolean(sectionMenuAnchorEl)}
                onClose={handleSectionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                    paper: {
                        sx: {
                            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px',
                            minWidth: '140px',
                            border: '1px solid #e5e7eb'
                        }
                    }
                }}
                sx={{
                    '& .MuiList-root': {
                        padding: '4px 0'
                    },
                }}
            >
                {/* <MenuItem 
                    onClick={() => {
                        handleEditSection(sectionMenuSection)
                        handleSectionEditClick(sectionMenuSection)
                        handleSectionMenuClose()
                    }} 
                    sx={{ 
                        padding: '8px 12px',
                        fontSize: '14px',
                        minHeight: 'auto',
                        '&:hover': {
                            backgroundColor: '#f3f4f6'
                        }
                    }}
                >
                    <EditIcon sx={{ mr: '8px', fontSize: '16px', color: '#6b7280' }} />
                    Edit
                </MenuItem> */}
                <MenuItem 
                    onClick={() => {
                        handleRenameSection(sectionMenuSection)
                        handleSectionMenuClose()
                    }} 
                    sx={{ 
                        padding: '8px 12px',
                        fontSize: '14px',
                        minHeight: 'auto',
                        '&:hover': {
                            backgroundColor: '#f3f4f6'
                        }
                    }}
                >
                    <DriveFileRenameOutlineIcon sx={{ mr: '8px', fontSize: '16px', color: '#6b7280' }} />
                    Rename
                </MenuItem>
                
                {/* <Divider /> */}

                {/* <MenuItem 
                    onClick={() => {
                        handleAddSectionBefore(sectionMenuSection)
                        handleSectionMenuClose()
                    }} 
                    sx={{ 
                        padding: '8px 12px',
                        fontSize: '14px',
                        minHeight: 'auto',
                        '&:hover': {
                            backgroundColor: '#f3f4f6'
                        }
                    }}
                >
                    <ArrowCircleUpIcon sx={{ mr: '8px', fontSize: '16px', color: '#6b7280' }} />
                    Add Section Above
                </MenuItem> */}
                {/* <MenuItem 
                    onClick={() => {
                        handleAddSectionAfter(sectionMenuSection)
                        handleSectionMenuClose()
                    }} 
                    sx={{ 
                        padding: '8px 12px',
                        fontSize: '14px',
                        minHeight: 'auto',
                        '&:hover': {
                            backgroundColor: '#f3f4f6'
                        }
                    }}
                >
                    <ArrowCircleDownIcon sx={{ mr: '8px', fontSize: '16px', color: '#6b7280' }} />
                    Add Section Below
                </MenuItem> */}

                <Divider sx={{ margin: '8px 0' }} />

                <MenuItem 
                    onClick={() => {
                        handleDeleteSection(sectionMenuSection)
                        // Note: Menu will be closed after dialog operation completes
                    }} 
                    sx={{ 
                        padding: '8px 12px',
                        fontSize: '14px',
                        minHeight: 'auto',
                        color: '#dc2626',
                        '&:hover': {
                            backgroundColor: '#fef2f2'
                        }
                    }}
                >
                    <DeleteIcon sx={{ mr: '8px', fontSize: '16px', color: '#dc2626' }} />
                    Delete
                </MenuItem>
            </Menu>

            <Modal open={error}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <h2>Error</h2>
                    <p>
                        There was an error loading the document. This document
                        may still be generating.
                    </p>
                    <button onClick={() => setError(false)}>Close</button>
                </Box>
            </Modal>
            {/* <Modal open={isAddSectionModalOpen} onClose={handleAddSectionModalClose}>
                <AddSectionModal 
                    onClose={ handleAddSectionModalClose } 
                    section={newSection}
                    setSectionName={handleAddSectionNameChange}
                    setSectionHeaderLevel={handleAddSectionHeaderLevelChange}
                    setSectionDescription={handleAddSectionSynopsisChange}
                    handleAddSection={handleAddSectionModalSubmit}
                />
            </Modal> */}

            {/* Training Set Modal */}
            <TrainingSetModal
                open={isTrainingSetModalOpen}
                onClose={handleTrainingSetModalClose}
                trainingSets={document.example_outline_ids || []}
                onRemoveTrainingSet={handleRemoveTrainingSet}
                onSave={handleSaveTrainingSets}
            />


            <DeleteTemplateConfirmationDialog
                open={isDeleteSectionDialogOpen}
                onClose={handleDeleteSectionDialogClose}
                onDelete={handleConfirmDeleteSection}
                loading={loading}
                sectionName={sectionMenuSection?.title || sectionMenuSection?.section_name || "selected section"}
            />

            <UsersAndPermissionsModal
                key={allUsers.length} // Force re-render when users count changes
                open={addUsersModalOpen}
                onClose={() => setAddUsersModalOpen(false)}
                users={allUsers}
                onRoleChange={handleRoleChange}
                onInvite={handleInviteUser}
                orgId={org}
                context="template"
                contextId={id}
                accessToken={auth.getAccessTokenSilently ? auth.getAccessTokenSilently() : null}
            />
        </Box>
    )
}

export default EditTemplatePage
